import { CircleCheck, Trash2, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@resource-kit/ui/react';
import { ANALYTICS_FRESHNESS, type AnalyticsFreshness } from './analyticsState';
import { ANALYTICS_PERIOD_KIND, type AnalyticsReportSummary } from './analyticsTypes';
import { formatDateTime } from './analyticsFormat';

export function ReportHeader({
  summary,
  label,
  freshness,
  onDelete,
  deleting = false,
}: {
  summary: AnalyticsReportSummary;
  label: string;
  freshness: AnalyticsFreshness | undefined;
  /** Present only when the caller may delete ad-hoc reports. */
  onDelete?: () => void;
  /** A delete is in flight: the delete control is disabled. */
  deleting?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const weekly = summary.period_kind === ANALYTICS_PERIOD_KIND.weekly;

  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold tracking-tight">{label}</h2>
          <Badge variant="secondary">
            {weekly ? t('analytics.period.weekly') : t('analytics.period.adhoc')}
          </Badge>
        </div>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            {t('analytics.report.generatedAt', {
              time: formatDateTime(summary.generated_at, i18n.resolvedLanguage),
            })}
          </span>
          {freshness?.kind === ANALYTICS_FRESHNESS.current && (
            <span role="status" className="flex items-center gap-1">
              <CircleCheck aria-hidden className="size-3.5 text-success" />
              {t('analytics.freshness.current')}
            </span>
          )}
          {freshness?.kind === ANALYTICS_FRESHNESS.stale && (
            <span role="status" className="flex items-center gap-1 text-destructive">
              <TriangleAlert aria-hidden className="size-3.5" />
              {t('analytics.freshness.stale')}
            </span>
          )}
        </p>
      </div>
      {!weekly && onDelete && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start text-muted-foreground hover:text-destructive"
          disabled={deleting}
          onClick={onDelete}
        >
          <Trash2 data-icon="inline-start" className="size-4" />
          {t('analytics.delete.button')}
        </Button>
      )}
    </header>
  );
}
